import React from "react";
import { motion } from "framer-motion";
import { Trophy, Sparkles, CheckCircle2, TrendingUp, Briefcase } from "lucide-react";

function Gauge({ score, label }) {
  const color = score >= 80 ? "#10B981" : score >= 50 ? "#FBBF24" : "#F87171";
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="flex flex-col items-center" data-testid={`case-study-gauge-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 -rotate-90">
          <circle cx="48" cy="48" r="40" stroke="rgba(255,255,255,0.08)" strokeWidth="7" fill="none" />
          <motion.circle
            cx="48" cy="48" r="40" stroke={color} strokeWidth="7" fill="none" strokeDasharray={circumference} strokeLinecap="round"
            initial={{ strokeDashoffset: circumference }} animate={{ strokeDashoffset: offset }} transition={{ duration: 1, ease: "easeOut" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-heading text-xl font-bold text-white">{score}</span>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2">{label}</p>
    </div>
  );
}

export default function CaseStudyFinalReport({ result, company }) {
  if (!result) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="case-study-final-report">
      <div className="glass-panel rounded-2xl p-6 border border-indigo-400/20 text-center" data-testid="case-study-portfolio-headline">
        <Trophy className="w-8 h-8 text-[#FBBF24] mx-auto mb-2" />
        <p className="text-sm font-semibold text-indigo-300">{result.portfolio_headline}</p>
        <p className="text-xs text-slate-400 mt-1 flex items-center justify-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> Now live on your public portfolio</p>
      </div>

      <div className="flex items-center justify-center gap-8 mt-6">
        <Gauge score={result.overall_score} label="Overall Score" />
        <Gauge score={result.simulator_score} label="Pipeline Score" />
        <Gauge score={result.sql_score} label="SQL Score" />
      </div>

      <div className="glass-panel rounded-2xl p-5 mt-6" data-testid="case-study-report-summary">
        <p className="text-sm text-slate-300 leading-relaxed">{result.summary}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div className="glass-panel rounded-2xl p-4" data-testid="case-study-report-strengths">
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1.5 mb-2"><CheckCircle2 className="w-3.5 h-3.5" /> Strengths</p>
          <ul className="space-y-1.5">{(result.strengths || []).map((s, i) => <li key={i} className="text-xs text-slate-400">• {s}</li>)}</ul>
        </div>
        <div className="glass-panel rounded-2xl p-4" data-testid="case-study-report-improvements">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5 mb-2"><TrendingUp className="w-3.5 h-3.5" /> Areas to Improve</p>
          <ul className="space-y-1.5">{(result.improvements || []).map((s, i) => <li key={i} className="text-xs text-slate-400">• {s}</li>)}</ul>
        </div>
      </div>

      {result.xp_awarded && (
        <div className="flex items-center justify-center gap-1.5 mt-6 text-sm font-bold text-[#FBBF24]" data-testid="case-study-xp-awarded">
          <Sparkles className="w-4 h-4" /> +{result.xp_awarded} XP earned for the {company} case study
        </div>
      )}
    </motion.div>
  );
}
